import React, { useState, useEffect } from 'react';
import { OptimizedImage } from '../common';
import styles from '../../styles/components/sections/GeneralSection.module.css';
import { replaceImageUrl } from '../../utils/helpers/imageUtils';
import { sanitizeHtml } from '../../utils/helpers/sanitize';
import { useSmoothScroll } from '../../utils/hooks/useSmoothScroll';
import { isSafari } from '../../utils/helpers/utils';

/**‰
 * A reusable general content section component that displays multi‰ple sections
 * Each section can have image on left or right with content on the opposite side
 *
 * @param {Object} props
 * @param {Array|Object} props.data - The content data for the sections (can be array or single object)
 * @param {String} props.className - Optional additional CSS class
 * @param {Number} props.globalIndex - Global index of this section across all sections on the page
 */

const GeneralSection = ({ data, className = '', globalIndex = 0 }) => {
    const { scrollToBooking } = useSmoothScroll();
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [isClient, setIsClient] = useState(false);

    // Set client-side flag
    useEffect(() => {
        setIsClient(true);
    }, []);

    // Detect system dark mode preference
    useEffect(() => {
        if (!isClient) return;

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        setIsDarkMode(mediaQuery.matches);

        const handleChange = e => {
            setIsDarkMode(e.matches);
            updateTheme(e.matches ? 'dark' : 'light');
        };

        mediaQuery.addEventListener('change', handleChange);

        // Check for saved theme preference
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
            setIsDarkMode(savedTheme === 'dark');
            updateTheme(savedTheme);
        } else {
            // Set initial theme based on system preference
            updateTheme(mediaQuery.matches ? 'dark' : 'light');
        }

        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [isClient]);

    // Detect mobile device
    useEffect(() => {
        if (!isClient) return;

        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, [isClient]);

    // Update theme on document
    const updateTheme = theme => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    };

    // Toggle theme manually
    const toggleTheme = () => {
        const newTheme = isDarkMode ? 'light' : 'dark';
        setIsDarkMode(!isDarkMode);
        updateTheme(newTheme);
    };

    if (!data) return null;

    // Helper function to create HTML from the rich text content
    const createMarkup = htmlContent => {
        const sanitizedContent = sanitizeHtml(htmlContent);
        return { __html: sanitizedContent };
    };

    // Convert single object to array for consistent processing
    const sections = Array.isArray(data) ? data : [data];

    // Filter out hidden sections and sort by order
    const visibleSections = sections
        .filter(section => !section.isHidden)
        .sort((a, b) => {
            const orderA = a.order || a.Order || 999;
            const orderB = b.order || b.Order || 999;
            return orderA - orderB;
        });

    if (visibleSections.length === 0) return null;

    const handleBook = () => scrollToBooking(100)

    return (
        <div className={`${className} ${styles['general-section']}`} >
            {visibleSections.map((sectionData, index) => {

                const hasImage = sectionData?.image?.image?.url;
                const imagePosition = sectionData?.image?.position;
                const isImageOnLeft = imagePosition === 'left';
                const isImageOnRight = imagePosition === 'right';
                const hasNoImage = !hasImage;


                return (
                    <section key={sectionData.id || index} className={`py-12 ${styles['section-spacing']}`}>
                        <div className="tw-container">
                            {/* Image on Left */}
                            {isImageOnLeft && (
                                <div className="row align-items-center">
                                    <div className="col-md-6 py-2">
                                        <OptimizedImage
                                            src={replaceImageUrl(sectionData?.image?.image?.url)}
                                            alt={sectionData?.image?.image?.alternativeText || sectionData?.title}
                                            className={styles['img-settings']}
                                            priority={false}
                                            loading="lazy"
                                            width={600}
                                            height={400}
                                            sizes="(max-width: 768px) 100vw, 50vw"
                                            quality={80}
                                        />
                                    </div>
                                    <div className="col-md-6 py-2">
                                        <div>
                                            <p className={styles['cc-headline']}>{sectionData.headline}</p>
                                            {globalIndex === 0 ? (
                                                <h1 className={styles['cc-heading']}>{sectionData.title}</h1>
                                            ) : (
                                                <h2 className={styles['cc-heading']}>{sectionData.title}</h2>
                                            )}
                                            <div className={styles['cc-description']} dangerouslySetInnerHTML={createMarkup(sectionData.description)} />
                                            {sectionData.BookingButton && (
                                                <div>
                                                    {!sectionData.BookingButton.isHidden && (
                                                        <div className="col-sm-4 mb-3">
                                                            <button
                                                                className="btn-form-active"
                                                                onClick={handleBook}
                                                                style={{
                                                                    filter: isDarkMode ? 'brightness(1.1) contrast(1.1)' : 'none',
                                                                }}>
                                                                {sectionData.BookingButton.name}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Image on Right */}
                            {isImageOnRight && (
                                <div className="row align-items-center px-0">
                                    <div className="col-md-6 !pr-0">
                                        <div>
                                            <p className={styles['cc-headline']}>{sectionData.headline}</p>
                                            {globalIndex === 0 ? (
                                                <h1 className={styles['cc-heading']}>{sectionData.title}</h1>
                                            ) : (
                                                <h2 className={styles['cc-heading']}>{sectionData.title}</h2>
                                            )}
                                            <div className={styles['cc-description']} dangerouslySetInnerHTML={createMarkup(sectionData.description)} />
                                            {sectionData.BookingButton && (
                                                <div>
                                                    {!sectionData.BookingButton.isHidden && sectionData.BookingButton.name && (
                                                        <div className="col-sm-4 mb-3">
                                                            <button
                                                                className="btn-form-active"
                                                                onClick={handleBook}
                                                                style={{
                                                                    filter: isDarkMode ? 'brightness(1.1) contrast(1.1)' : 'none',
                                                                }}>
                                                                {sectionData.BookingButton.name}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <OptimizedImage
                                            src={replaceImageUrl(sectionData?.image?.image?.url)}
                                            alt={sectionData?.image?.image?.alternativeText || sectionData?.title}
                                            className={styles['img-settings']}
                                            priority={index === 0 && globalIndex === 0}
                                            width={600}
                                            height={400}
                                            sizes="(max-width: 768px) 100vw, 50vw"
                                            quality={80}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* No Image */}
                            {hasNoImage && (
                                <div className="row align-items-center">
                                    <div className="col-md-12">
                                        <div>
                                            <p className={styles['cc-headline']}>{sectionData.headline}</p>
                                            {globalIndex === 0 ? (
                                                <h1 className={styles['cc-heading']}>{sectionData.title}</h1>
                                            ) : (
                                                <h2 className={styles['cc-heading']}>{sectionData.title}</h2>
                                            )}
                                            <div className={`${styles['cc-description']} ${styles['no-image-content']}`} dangerouslySetInnerHTML={createMarkup(sectionData.description)} />
                                            {sectionData.BookingButton && (
                                                <div>
                                                    {!sectionData.BookingButton.isHidden && sectionData.BookingButton.name && (
                                                        <div className="col-sm-4">
                                                            <button
                                                                className="btn-form-active"
                                                                onClick={handleBook}
                                                                style={{
                                                                    filter: isDarkMode ? 'brightness(1.1) contrast(1.1)' : 'none',
                                                                }}>
                                                                {sectionData.BookingButton.name}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

export default GeneralSection;
